import { CHAT_GENIE_FEATURES } from '../constant/rbacConfig';
import { RBAC_ENABLED } from '../constant/globalConstants';

const cap = (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

/**
 * Returns a human-readable model name for a given deployment ID.
 *
 * Resolution order:
 *  1. Direct match from `models` array (API data — most accurate)
 *  2. Regex pattern matching by model family (Gemini, Claude, GPT, Grok)
 *  3. Fallback → "Auto-Select"
 *
 * @param {string} deploymentId - Raw Azure / provider deployment ID.
 * @param {Array}  [models=[]]  - Model objects from the API ({ name, key, label }).
 * @returns {string}
 */
export function getModelInfo(deploymentId = "", models = []) {
  if (!deploymentId) return "Auto-Select";

  // Already a human-readable name (e.g. "Claude Sonnet 4.6") — return as-is
  if (deploymentId.includes(" ")) return deploymentId;

  // Exact match from API models list (name or label)
  const exactMatch = models.find(
    (m) =>
      m?.name?.toLowerCase() === deploymentId.toLowerCase()
  );
  if (exactMatch) return exactMatch.name || null;

  const id = deploymentId.toLowerCase();

  // Gemini
  const geminiMatch = id.match(/gemini-([\d.]+)(?:-(flash|pro|ultra|nano|exp))?/);
  if (geminiMatch) {
    const variant = geminiMatch[2] ? ` ${cap(geminiMatch[2])}` : "";
    return `Gemini ${geminiMatch[1]}${variant}`;
  }

  // Claude — handles "46" (concatenated) → "4.6" and "4-5" (dash-sep) → "4.5"
  const claudeMatch = id.match(/claude-(sonnet|opus|haiku|instant|lite)-(\d+)(?:-(\d+))?/);
  if (claudeMatch) {
    const variant = cap(claudeMatch[1]);
    const rawMajor = claudeMatch[2];
    const rawMinor = claudeMatch[3];
    const version = rawMinor
      ? `${rawMajor}.${rawMinor}`
      : rawMajor.length === 2 && !rawMajor.startsWith("0")
        ? `${rawMajor[0]}.${rawMajor[1]}`
        : rawMajor;
    return `Claude ${variant} ${version}`;
  }

  // GPT-4o mini (must precede GPT-4o check)
  if (id.includes("gpt-4o-mini")) return "GPT-4o mini";

  // GPT-4o
  if (id.includes("gpt-4o")) return "GPT-4o";

  // o-series (o1, o3-mini, o4, …)
  const oSeriesMatch = id.match(/(?:^|-)o(\d+)(?:-(mini|pro|nano))?/);
  if (oSeriesMatch) {
    const variant = oSeriesMatch[2] ? ` ${oSeriesMatch[2]}` : "";
    return `GPT-o${oSeriesMatch[1]}${variant}`;
  }

  // GPT-N — handles "52" (concatenated) → "5.2" and "5-2" (dash-sep) → "5.2"
  const gptNMatch = id.match(/gpt-(\d+)(?:-(\d+))?/);
  if (gptNMatch) {
    const rawMajor = gptNMatch[1];
    const rawMinor = gptNMatch[2];
    const version = rawMinor
      ? `${rawMajor}.${rawMinor}`
      : rawMajor.length === 2 && !rawMajor.startsWith("0")
        ? `${rawMajor[0]}.${rawMajor[1]}`
        : rawMajor;
    return `GPT-${version}`;
  }

  // Grok
  const grokMatch = id.match(/grok-(\d+)(?:-(\d+))?/);
  if (grokMatch) {
    const rawMajor = grokMatch[1];
    const rawMinor = grokMatch[2];
    const version = rawMinor
      ? `${rawMajor}.${rawMinor}`
      : rawMajor.length === 2 && !rawMajor.startsWith("0")
        ? `${rawMajor[0]}.${rawMajor[1]}`
        : rawMajor;
    return `Grok ${version}`;
  }

  return null;
}

/**
 * Filters models based on Chat Genie RBAC features.
 * Models without a matching feature key are shown by default.
 *
 * @param {Array}  models          - Model objects with a 'key' property.
 * @param {Object} enabledFeatures - Feature flags map (e.g. { auto_select: true }).
 * @returns {Array}
 */
export function filterModelsByFeatures(models = [], enabledFeatures = {}) {
  const hasFeatures = Object.keys(enabledFeatures).length > 0;

  if (!models?.length || !RBAC_ENABLED || !hasFeatures) return models;

  const featureValues = Object.values(CHAT_GENIE_FEATURES);

  return models.filter((model) => {
    const matchingFeature = featureValues.find(f => f === model?.key);
    return !model?.key || !matchingFeature || enabledFeatures[matchingFeature] === true;
  });
}